import { useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ProgressSpinner } from "primereact/progressspinner";
import { RootState } from "app/store";
import { CONTACT_PROFILE_LOOKUP } from "app/redux";
import {
  getObjectFromSessionStore,
  getSessionStore,
  setObjectInSessionStore,
  setSessionStore,
} from "app/utils/sessionStore";
import { useIPContext } from "app/pages/InteractiveProposal/InteractiveProposalProvider";
import PageHeader from "app/pages/InteractiveProposal/InteractiveProposalHeader";
import InteractiveReviewCenterSidebar from "./InteractiveReviewCenterSidebar";
import { makeRequest } from "app/utils/makeRequest";
import { ProposalTypes } from "app/utils/types";

const InteractiveReviewCenterLayout = () => {
  const lookUpResponse = useSelector((state: RootState) => state?.contactProfileLookup);
  const { isLoading } = lookUpResponse;
  const dispatch = useDispatch();

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isCommentMode, setIsCommentMode] = useState(false);
  const mainContentRef = useRef<HTMLDivElement>(null);
  const [selectedYear, setSelectedYear] = useState<number>(
    () => getSessionStore("ircSelectedYear") ?? 1,
  );
  const [animationData, setAnimationData] = useState<any>(null);
  const lottieRef = useRef<any>(null);
  const [selectedCreditType, setSelectedCreditType] = useState("preCredit");

  const { summaryData: proposalSummary, summaryLoading } = useIPContext();
  const getlookUpResponse = getObjectFromSessionStore("lookUpResponse");
  const location = useLocation();
  const pathName = location.pathname.split("/")[2];
  const planId = getSessionStore("planInfo")?.planId;
  const [summaryData, setSummaryData] = useState<any | null>(null);
  const [, setLoading] = useState(false);

  const headerTitle = summaryData?.entity?.title ?? proposalSummary?.employerName;
  const headerSubtitle = summaryData?.contacts
    ? `${summaryData.contacts.profile.firstName ?? ""} ${
        summaryData.contacts.profile.lastName ?? ""
      }`.trim()
    : `${proposalSummary?.primaryContact?.firstName ?? ""} ${
        proposalSummary?.primaryContact?.lastName ?? ""
      }`.trim();
  const headerProposalNo = summaryData?.proposalNo ?? proposalSummary?.proposalNo;
  const headerProposalType = proposalSummary?.proposalType ?? summaryData?.planType;

  const animationURL = (type?: string) => {
    if (type === ProposalTypes.KeyPersonInsurance) {
      return "https://iapp.alexmelik.com/wp-content/uploads/2024/08/Key-Person.json";
    }
    if (type === ProposalTypes.BuySell) {
      return "https://iapp.alexmelik.com/wp-content/uploads/2023/09/Buy-Sell.json";
    }
    if (type === ProposalTypes.PremiumFinance) {
      return "https://iapp.alexmelik.com/wp-content/uploads/2023/09/Premium-Finance.json";
    }
    return "https://iapp.alexmelik.com/wp-content/uploads/2023/09/Split-Dollar.json";
  };

  useEffect(() => {
    if (!headerProposalType) return;
    fetch(animationURL(headerProposalType))
      .then((res) => res.json())
      .then((data) => setAnimationData(data));
  }, [headerProposalType]);

  // Scroll to top on route change
  useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
  }, [pathName]);

  const formatCreditTypeLabel = (ct: string) =>
    ct === "postCredit" ? "Post-Credit" : "Pre-Credit";

  function mapReviewsToSteps(
    reviews: any[],
    selectedYearValue: number,
    selectedCreditTypeValue: string,
  ): any[] {
    return reviews.map((r) => {
      if (r.planYear === selectedYearValue) {
        return {
          yearNumber: r.planYear,
          calendarYear: r.year,
          status: "CURRENT",
          label: formatCreditTypeLabel(selectedCreditTypeValue),
        };
      }
      const storedDate = getSessionStore("currentDate");
      const now = storedDate === true ? new Date(r.reviewOpenDate!).getTime() + 1 : Date.now();
      if (
        r.reviewOpenDate &&
        r.anniversaryDate &&
        (now < new Date(r.reviewOpenDate).getTime() || now > new Date(r.anniversaryDate).getTime())
      ) {
        return { yearNumber: r.planYear, calendarYear: r.year, status: "LOCKED" };
      }
      if (r.confirmedBy > 0 && r.skippedBy >= 0) {
        return {
          yearNumber: r.planYear,
          calendarYear: r.year,
          status: "COMPLETED",
          label: "Confirm(#)",
        };
      }
      if (r.total > 0 && r.confirmedBy === r.total) {
        return {
          yearNumber: r.planYear,
          calendarYear: r.year,
          status: "COMPLETED",
          label: "Confirm All",
        };
      }
      if (r.total > 0 && r.skippedBy === r.total) {
        return {
          yearNumber: r.planYear,
          calendarYear: r.year,
          status: "SKIPPED",
          label: "Skip (All)",
        };
      }
      if (r.confirmedBy > 0) {
        return {
          yearNumber: r.planYear,
          calendarYear: r.year,
          status: "COMPLETED",
          label: `Confirm (${r.confirmedBy})`,
        };
      }
      if (r.skippedBy > 0) {
        return {
          yearNumber: r.planYear,
          calendarYear: r.year,
          status: "SKIPPED",
          label: `Skip (${r.skippedBy})`,
        };
      }
      return { yearNumber: r.planYear, calendarYear: r.year, status: "LOCKED" };
    });
  }

  // Ensure lookup data is available for header branding
  useEffect(() => {
    if (!getlookUpResponse?.menu) {
      dispatch({
        type: CONTACT_PROFILE_LOOKUP,
        payload: { address: "lookupAfter" },
      });
    }
  }, []);

  useEffect(() => {
    if (lookUpResponse?.data?.menu) {
      setObjectInSessionStore("lookUpResponse", lookUpResponse?.data);
    }
  }, [lookUpResponse]);

  useEffect(() => {
    const handler = (e: Event) => {
      const { detail } = e as CustomEvent;
      if (detail?.year != null) {
        setSelectedYear(detail.year);
        setSessionStore("ircSelectedYear", detail.year);
      }
    };
    window.addEventListener("ircSelectionChanged", handler);
    return () => window.removeEventListener("ircSelectionChanged", handler);
  }, []);

  useEffect(() => {
    if (!planId) return;
    let cancelled = false;

    setLoading(true);
    makeRequest("interactiveReviewCenterSummary")
      .get(`${planId}?year=${selectedYear}&creditType=${selectedCreditType}`, true)
      .then((res: any) => {
        if (cancelled) return;
        const data: any = res?.data?.data;
        setSummaryData(data);
      })
      .catch((err: any) => {
        if (!cancelled) console.error("Failed to fetch review center summary:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    // eslint-disable-next-line
    return () => {
      cancelled = true;
    };
  }, [planId, selectedYear, selectedCreditType]);

  const yearSteps = useMemo(
    () =>
      summaryData
        ? mapReviewsToSteps(summaryData.planReviews, selectedYear, selectedCreditType)
        : [],
    [summaryData, selectedYear, selectedCreditType],
  );
  const headerLoadingPage = isLoading;

  const loadingPage = isLoading || summaryLoading;

  const mainContent = () => {
    if (loadingPage) {
      return (
        <div className="flex justify-center items-center h-full">
          <ProgressSpinner />
        </div>
      );
    }
    return <Outlet />;
  };

  return (
    <div className={`flex h-screen ${isCommentMode ? "comment-mode" : ""}`}>
      <PageHeader
        title={headerTitle}
        subtitle={headerSubtitle}
        proposalNo={headerProposalNo}
        proposalType={headerProposalType}
        error={null}
        loadingPage={headerLoadingPage}
        isCollapsed={isCollapsed}
        animationData={animationData}
        setIsHovered={() => {}}
        lottieRef={lottieRef}
        setIsCommentMode={setIsCommentMode}
        isCommentMode={isCommentMode}
        yearData={yearSteps}
        creditTypeData={summaryData?.selectedCreditType ?? selectedCreditType}
        onPhaseClick={(phase) => {
          setSelectedCreditType(phase);
          setSessionStore("ircSelectedCreditType", phase);
          window.dispatchEvent(
            new CustomEvent("ircSelectionChanged", { detail: { creditType: phase } }),
          );
        }}
      />

      <InteractiveReviewCenterSidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />
      <div
        ref={mainContentRef}
        className="flex-1 overflow-auto overflow-x-hidden h-[calc(100vh-70px)] mt-auto py-4 pl-6 pr-4 scrollbar-hide"
      >
        {mainContent()}
      </div>
    </div>
  );
};

export default InteractiveReviewCenterLayout;
